import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  Users,
  // Heart,
  LifeBuoy,
  ChevronDown,
  ChevronLeft,
  CreditCard,
  X,
  FileText,
  Gift,
  Tag,
  Home,
  Video,
  Image,
  ShieldAlert,
  ShieldCheck,
  Disc,
  Settings as SettingsIcon,
  Sun,
  Moon,
} from "lucide-react";

const menuItems = [
  {
    section: "MAIN",
    items: [
      { name: "Dashboard", icon: LayoutDashboard, path: "/admin" },
    ]
  },
  {
    section: "USER MANAGEMENT",
    items: [
      { name: "Users", icon: Users, path: "/admin/users" },
      { name: "KYC Verifications", icon: ShieldCheck, path: "/admin/kyc" },
      { name: "Abuse Reports & Blocks", icon: ShieldAlert, path: "/admin/abuse-reports" },
      // { name: "Bookmarks", icon: FileText, path: "/admin/bookmarks" },
      // { name: "Likes", icon: Heart, path: "/admin/likes" },
    ]
  },
  {
    section: "PAGES",
    items: [
      { name: "Pages", icon: FileText, path: "/admin/pages" },
      { name: "FAQs", icon: FileText, path: "/admin/faqs" },
      { name: "Offers", icon: Tag, path: "/admin/offers" },
      { name: "Gifts", icon: Gift, path: "/admin/gifts" },
      { name: "Avatars", icon: Image, path: "/admin/avatars" },
      { name: "Rooms", icon: Home, path: "/admin/rooms" },
      { name: "Live Streaming", icon: Video, path: "/admin/live-streaming" },
    ]
  },
  {
    section: "SUPPORT",
    items: [
      { name: "Support", icon: LifeBuoy, path: "/admin/support" },
      { name: "Notification", icon: LifeBuoy, path: "/admin/notifications" },
    ]
  },
  {
    section: "MONETIZATION",
    items: [
      { name: "Signup Reward", icon: Gift, path: "/admin/signup-reward" },
      { name: "Spin Wheel", icon: Disc, path: "/admin/spin-wheel" },
      { name: "Withdrawals", icon: CreditCard, path: "/admin/withdrawals" },
      { name: "Coin Records", icon: CreditCard, path: "/admin/coin-records" },
      { name: "Currencies", icon: CreditCard, path: "/admin/currencies" },
    ]
  },
  {
    section: "SETTINGS",
    items: [
      { name: "Settings", icon: SettingsIcon, path: "/admin/settings" },
    ]
  },
];

export default function Sidebar({ isCollapsed, toggleSidebar, isMobileOpen, closeMobileSidebar }) {
  const location = useLocation();
  const { toggleTheme, isDark } = useTheme();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  // On mobile screen drawer, always render full expanded view like desktop
  const isEffectiveCollapsed = isCollapsed && !isMobileOpen;

  const toggleSubmenu = (name) => {
    setOpenSubmenu(openSubmenu === name ? null : name);
  };

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    // Close mobile sidebar when clicking a link
    if (window.innerWidth < 1024) {
      closeMobileSidebar?.();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
          onClick={closeMobileSidebar}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-screen z-50 transition-all duration-300 ease-in-out flex flex-col shadow-2xl border-r
            ${isDark ? 'border-slate-800' : 'border-gray-200'}
            ${isEffectiveCollapsed ? 'w-[70px]' : 'w-[260px] xl:w-[280px]'}
            ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{
          background: isDark
            ? 'linear-gradient(180deg, #0e1726 0%, #1a2a47 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        }}
      >
        {/* Logo Section */}
        <div className={`flex items-center justify-between h-14 xl:h-16 border-b shrink-0 overflow-hidden transition-all ${
          isEffectiveCollapsed ? 'px-2' : 'px-3 xl:px-4'
        } ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          <div className={`flex items-center ${isEffectiveCollapsed ? 'gap-1' : 'gap-2 xl:gap-3'}`}>
            <div className={`rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
              isEffectiveCollapsed ? 'w-7 h-7' : 'w-8 h-8 xl:w-9 xl:h-9'
            }`}>
              <img src="../../logo512.png" alt="logo512.png" className="w-full h-full object-contain" />
            </div>
            {!isEffectiveCollapsed && (
              <span className={`text-lg xl:text-xl font-bold animate-fadeIn whitespace-nowrap ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Admin Panel
              </span>
            )}
          </div>
          <div className="flex items-center">
            {/* Mobile Close Button */}
            <button
              onClick={closeMobileSidebar}
              className={`lg:hidden p-1.5 rounded-lg transition-colors ${
                isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
            {/* Desktop Collapse Button */}
            <button
              onClick={toggleSidebar}
              className={`hidden lg:flex items-center justify-center rounded-lg transition-colors ${
                isEffectiveCollapsed ? 'p-1' : 'p-1.5'
              } ${
                isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
              title={isEffectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <ChevronLeft className={`transition-transform duration-300 ${
                isEffectiveCollapsed ? 'w-3.5 h-3.5 rotate-180' : 'w-4 h-4 xl:w-5 xl:h-5'
              }`} />
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto py-3 xl:py-4 px-2 xl:px-3 custom-scrollbar">
          {menuItems.map((section, sectionIndex) => (
            <div key={section.section} className={sectionIndex > 0 ? 'mt-4 xl:mt-6' : ''}>
              {!isEffectiveCollapsed && (
                <p className={`px-2 xl:px-3 mb-1.5 xl:mb-2 text-[10px] xl:text-xs font-semibold uppercase tracking-wider truncate ${
                  isDark ? 'text-gray-400' : 'text-gray-400'
                }`}>
                  {section.section}
                </p>
              )}

              <ul className="space-y-0.5 xl:space-y-1">
                {section.items.map((item) => (
                  <li key={item.name}>
                    {item.submenu ? (
                      <div>
                        <button
                          onClick={() => toggleSubmenu(item.name)}
                          className={`w-full flex items-center gap-2 xl:gap-3 px-2 xl:px-3 py-2 xl:py-2.5 rounded-lg transition-all duration-200 group
                              ${openSubmenu === item.name
                              ? (isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900 font-medium')
                              : (isDark ? 'text-gray-400 hover:bg-white/5 hover:text-white' : 'text-gray-600 hover:bg-gray-100/70 hover:text-gray-900')}`}
                        >
                          <item.icon className="w-4 h-4 xl:w-5 xl:h-5 flex-shrink-0" />
                          {!isEffectiveCollapsed && (
                            <>
                              <span className="flex-1 text-left text-xs xl:text-sm font-medium truncate">{item.name}</span>
                              <ChevronDown
                                className={`w-3 h-3 xl:w-4 xl:h-4 transition-transform duration-200 flex-shrink-0
                                    ${openSubmenu === item.name ? 'rotate-180' : ''}`}
                              />
                            </>
                          )}
                        </button>

                        {!isEffectiveCollapsed && openSubmenu === item.name && (
                          <ul className="mt-1 ml-6 xl:ml-8 space-y-0.5 xl:space-y-1 animate-slideInUp">
                            {item.submenu.map((subitem) => (
                              <li key={subitem.path}>
                                <NavLink
                                  to={subitem.path}
                                  onClick={handleNavClick}
                                  className={`block px-2 xl:px-3 py-1.5 xl:py-2 rounded-lg text-xs xl:text-sm transition-colors
                                      ${isActive(subitem.path)
                                      ? (isDark ? 'text-blue-400 bg-blue-500/10 font-medium' : 'text-blue-600 bg-blue-50 font-medium')
                                      : (isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')}`}
                                >
                                  {subitem.name}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <NavLink
                        to={item.path}
                        onClick={handleNavClick}
                        className={`flex items-center gap-2 xl:gap-3 px-2 xl:px-3 py-2 xl:py-2.5 rounded-lg transition-all duration-200 group relative
                            ${isActive(item.path)
                            ? (isDark 
                                ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/10 text-white font-medium' 
                                : 'bg-gradient-to-r from-blue-50 to-indigo-50/70 text-blue-600 font-semibold shadow-xs')
                            : (isDark 
                                ? 'text-gray-400 hover:bg-white/5 hover:text-white' 
                                : 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-900')}`}
                      >
                        {isActive(item.path) && (
                          <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 xl:w-1 h-5 xl:h-6 rounded-r-full ${
                            isDark ? 'bg-gradient-to-b from-blue-500 to-purple-500' : 'bg-blue-600'
                          }`} />
                        )}
                        <item.icon className={`w-4 h-4 xl:w-5 xl:h-5 flex-shrink-0 transition-colors
                            ${isActive(item.path) 
                              ? (isDark ? 'text-blue-400' : 'text-blue-600') 
                              : (isDark ? 'group-hover:text-blue-400' : 'group-hover:text-blue-600')}`} />
                        {!isEffectiveCollapsed && (
                          <>
                            <span className="flex-1 text-xs xl:text-sm font-medium truncate">{item.name}</span>
                            {item.badge && (
                              <span className="px-1.5 xl:px-2 py-0.5 text-[10px] xl:text-xs font-semibold rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex-shrink-0">
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer with Dark/Light Mode Toggle */}
        {!isEffectiveCollapsed ? (
          <div className={`w-full shrink-0 border-t ${
            isDark ? 'border-white/10 bg-[#0e1726]/80' : 'border-gray-200 bg-white/90'
          } backdrop-blur-md z-10 mt-auto transition-colors`}>
            <div className="p-2.5 xl:p-3">
              {/* Theme Toggle Button */}
              <div
                onClick={toggleTheme}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleTheme(); }}
                className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-all duration-200 select-none group ${
                  isDark 
                    ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                    : 'bg-gray-100 hover:bg-gray-200/70 border border-gray-200/80'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    isDark ? 'bg-amber-400/10 text-amber-400' : 'bg-blue-500/10 text-blue-600'
                  }`}>
                    {isDark ? (
                      <Moon className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                    ) : (
                      <Sun className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                    )}
                  </div>
                  <div className="flex flex-col text-left">
                    <span className={`text-xs font-semibold leading-none ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {isDark ? 'Dark Mode' : 'Light Mode'}
                    </span>
                    <span className={`text-[10px] mt-0.5 leading-none ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {isDark ? 'Switch to light' : 'Switch to dark'}
                    </span>
                  </div>
                </div>

                {/* Animated Switch Pill */}
                <div
                  className={`w-11 h-6 flex items-center rounded-full p-0.5 transition-colors duration-300 ${
                    isDark ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${
                      isDark ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  >
                    {isDark ? (
                      <Moon className="w-2.5 h-2.5 text-blue-600" />
                    ) : (
                      <Sun className="w-2.5 h-2.5 text-amber-500" />
                    )}
                  </div>
                </div>
              </div>

              <p className={`mt-2 text-[10px] xl:text-xs text-center transition-colors ${
                isDark ? 'text-gray-500' : 'text-gray-400'
              }`}>
                © 2026 DatingApp
              </p>
            </div>
          </div>
        ) : (
          /* Collapsed Icon-Only Theme Toggle */
          <div className={`w-full shrink-0 p-2.5 border-t ${
            isDark ? 'border-white/10 bg-[#0e1726]/80' : 'border-gray-200 bg-white/90'
          } flex flex-col items-center justify-center mt-auto transition-colors`}>
            <button
              type="button"
              onClick={toggleTheme}
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              className={`p-2.5 rounded-xl transition-all duration-200 group ${
                isDark
                  ? 'hover:bg-white/10 text-gray-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-amber-400 group-hover:rotate-90 transition-transform duration-300" />
              ) : (
                <Moon className="w-5 h-5 text-blue-600 group-hover:-rotate-12 transition-transform duration-300" />
              )}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}